import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useLang, CURRENCIES, LANGUAGES } from '@/context/LangContext';
import { FlashGrowthSection } from '@/components/FlashGrowthSection';
import PageSEO from '@/components/PageSEO';
import NewsletterSignup from '@/components/NewsletterSignup';
import {
  Sun, Moon, Zap, BarChart3, Brain, Calendar, Bot, ArrowRight, Check, ChevronRight,
  Shield, Globe, Cpu, TrendingUp, Eye, Target, AlertTriangle, Star, Users, Building2,
  Layers, Sparkles, Play, X, Mail, CreditCard, Lock, Server, Database, Plug, BookOpen, UserCog, Menu
} from 'lucide-react';

export default function LandingPage() {
  const { isDark, toggleTheme, colors: T } = useTheme();
  const { t, lang, setLang, currency, setCurrency, fmtUSD } = useLang();
  const navigate = useNavigate();
  const [showTrial, setShowTrial] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') { setShowTrial(false); setMenuOpen(false); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const [email, setEmail] = useState('');
  const [annual, setAnnual] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const startTrial = () => {
    if (email) { navigate('/auth'); }
  };

  const ecosystem = [
    { name:'OmniView', desc:'Command Center Dashboard', detail:'Unify 28+ data sources. Real-time KPIs, blind spots, and revenue recovery across every brand.', icon:Eye, color:'#00D4FF', tag:'Dashboard' },
    { name:'OmniThink', desc:'Predictive Analytics', detail:'AI-powered insights using Qwen 3.6 & Gemma 4. Spot patterns humans miss. Forecast with precision.', icon:Brain, color:'#C77DFF', tag:'Analytics' },
    { name:'OmniPlan', desc:'Smart Media Planning', detail:'Auto-generate media plans across 16+ platforms. Budget allocation, audience targeting, creative strategy.', icon:Calendar, color:'#FFD166', tag:'Planning' },
    { name:'OmniClaw', desc:'Media Plan Deploying Agent', detail:'Deploy, monitor, optimize. Autonomous agent that executes plans, manages bids, and reports in real-time.', icon:Bot, color:'#06D6A0', tag:'Agent' },
  ];

  const caseStudies = [
    { title:'FMCG Brand - Q-Commerce Dominance', industry:'FMCG / Quick Commerce', results:[ {metric:'340%',label:'ROAS Increase'},{metric:'12x',label:'SOV Growth'},{metric:'$340K',label:'Revenue Recovered'} ],
      detail:'Identified underallocated Q-commerce budget through OmniView blind spots. Shifted spend to Blinkit + Zepto. Achieved category #1 in 90 days.', color:'#FF6B6B', period:'6 months' },
    { title:'D2C Beauty - Pipeline Explosion', industry:'Beauty / D2C', results:[ {metric:'12x',label:'Pipeline Growth'},{metric:'52%',label:'Lower CAC'},{metric:'4.7x',label:'LTV:CAC Ratio'} ],
      detail:'OmniThink identified TikTok audience gap. OmniClaw deployed UGC campaign. Combined with Nykaa SOV strategy = category dominance.', color:'#C77DFF', period:'4 months' },
    { title:'B2B SaaS - Enterprise Pipeline', industry:'B2B SaaS', results:[ {metric:'$1.0M',label:'Pipeline Created'},{metric:'31%',label:'SQL Rate'},{metric:'18d',label:'Faster Close'} ],
      detail:'LinkedIn ABM strategy designed by OmniPlan. CRM integration revealed MQL-SQL bottleneck. Fixed with automated nurture = 3x close rate.', color:'#4285F4', period:'5 months' },
    { title:'AgriTech - Market Penetration', industry:'AgriTech / Rural', results:[ {metric:'280%',label:'User Growth'},{metric:'68%',label:'NTB Rate'},{metric:'$145K',label:'Monthly GMV'} ],
      detail:'OmniView revealed missed rural search demand. OmniClaw deployed hyper-local campaigns across Google + regional platforms. First-mover advantage.', color:'#F77F00', period:'8 months' },
  ];

  const cx = CURRENCIES[currency];
  // USD-base pricing (annual = ~20% discount). EUR/INR auto-converted via fmtUSD.
  const pricing = [
    { name:'Starter', price:fmtUSD(annual?8:10), period:'/mo', desc:'For solo founders & small brands', color:'#00D4FF',
      features:['1 Brand','3 Channels','OmniView Dashboard','Basic Analytics','Email Support','Community Access'],
      cta:'Start Free Trial', popular:false },
    { name:'Pro', price:fmtUSD(annual?39:49), period:'/mo', desc:'For growing teams & agencies', color:'#C77DFF',
      features:['5 Brands','15 Channels','OmniView + OmniThink','Advanced Analytics','Priority Support','API Access','Custom Reports'],
      cta:'Start Free Trial', popular:true },
    { name:'Enterprise', price:fmtUSD(annual?159:199), period:'/mo', desc:'For large agencies & enterprises', color:'#FFD166',
      features:['Unlimited Brands','All Channels','Full Suite (View, Think, Plan, Claw)','AI Agents','Dedicated Manager','SLA 99.9%','White Label','Custom Integrations'],
      cta:'Start Free Trial', popular:false },
    { name:'Bespoke', price:'Custom', period:'', desc:'Done-With-You setup. Grandfather pricing.', color:'#06D6A0',
      features:['Everything in Enterprise','CRM Setup & Integration','Stock Tracking & Alerts','Market Spying Module','Competitor Intelligence','Open Source Self-Hosted Option','Qwen 3.6 / Gemma 4 Powered','Lifetime Grandfather Rate'],
      cta:'Talk to Us', popular:false },
  ];

  const steps = [
    { step:'01', title:t('steps.s1.title'), desc:t('steps.s1.desc'), icon:Zap, color:'#00D4FF' },
    { step:'02', title:t('steps.s2.title'), desc:t('steps.s2.desc'), icon:Bot, color:'#C77DFF' },
    { step:'03', title:t('steps.s3.title'), desc:t('steps.s3.desc'), icon:TrendingUp, color:'#06D6A0' },
  ];

  // "How it Works" — graphical architecture story (humans → connectors → LLM → agent → encrypted vault)
  const howSteps = [
    { icon:UserCog,  title:t('how.step1.title'), desc:t('how.step1.desc'), color:'#00D4FF' },
    { icon:Plug,     title:t('how.step2.title'), desc:t('how.step2.desc'), color:'#C77DFF' },
    { icon:BookOpen, title:t('how.step3.title'), desc:t('how.step3.desc'), color:'#FFD166' },
    { icon:Bot,      title:t('how.step4.title'), desc:t('how.step4.desc'), color:'#06D6A0' },
  ];

  return (
    <div style={{ fontFamily:"'Outfit',sans-serif" }}
      className={`min-h-screen ${isDark ? 'bg-zinc-950 text-zinc-100' : 'bg-white text-zinc-900'}`}>
      <PageSEO title="Marketing Intelligence Platform" description="OmniView — The Operating System for Marketing Intelligence. Unify 28+ channels, spot blind spots, deploy AI agents. From $10/mo." keywords="marketing intelligence platform, OmniView, media planning, ROAS optimization, marketing dashboard, digital advertising analytics, OmniThink, OmniPlan, OmniClaw" />

      {/* ── NAV ── */}
      <nav data-testid="landing-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'glass' : ''}`}
        style={{ background: scrolled ? (isDark ? 'rgba(9,9,11,0.97)' : 'rgba(255,255,255,0.97)') : 'transparent',
          borderBottom: scrolled ? `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` : 'none' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
              style={{ background:'linear-gradient(135deg,#002FA7,#00D4FF)' }}>{'\u03A9'}</div>
            <span className="text-lg font-bold tracking-tight">OMNI<span style={{color:'#00D4FF'}}>VIEW</span></span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            {[
              { key:'nav.products', href:'#products' },
              { key:'nav.flash_growth', href:'#flash-growth', label:'Flash Growth' },
              { key:'nav.pricing', href:'#pricing' },
              { key:'nav.case_studies', href:'#case-studies' },
            ].map(item =>
              <a key={item.key} href={item.href}
                className={`text-sm font-semibold transition-colors ${isDark ? 'text-zinc-300 hover:text-white' : 'text-zinc-600 hover:text-zinc-900'}`}>{item.label || t(item.key)}</a>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language Switcher */}
            <div className="relative">
              <button data-testid="lang-toggle" onClick={() => setLangOpen(o => !o)}
                aria-label={t('common.language')}
                className={`h-9 px-2.5 rounded-md flex items-center gap-1 border text-xs font-bold transition-all ${isDark ? 'border-zinc-800 hover:border-zinc-600 text-zinc-300' : 'border-zinc-200 hover:border-zinc-400 text-zinc-700'}`}>
                <Globe size={13}/> {lang.toUpperCase()}
              </button>
              {langOpen && (
                <div data-testid="lang-menu" className={`absolute right-0 mt-2 w-44 rounded-md border shadow-lg z-50 overflow-hidden ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
                  {LANGUAGES.map(L => (
                    <button key={L.code} data-testid={`lang-${L.code}`}
                      onClick={() => { setLang(L.code); setLangOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between ${lang===L.code ? (isDark?'bg-zinc-800 text-white':'bg-zinc-100 text-zinc-900') : (isDark?'text-zinc-300 hover:bg-zinc-800':'text-zinc-700 hover:bg-zinc-50')}`}>
                      <span>{L.label}</span>
                      <span className={`text-[10px] font-bold ${lang===L.code?'opacity-100':'opacity-50'}`}>{L.flag}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button data-testid="landing-theme-toggle" onClick={toggleTheme}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              className={`w-9 h-9 rounded-md flex items-center justify-center border transition-all ${isDark ? 'border-zinc-800 hover:border-zinc-600 text-zinc-400' : 'border-zinc-200 hover:border-zinc-400 text-zinc-500'}`}>
              {isDark ? <Sun size={16}/> : <Moon size={16}/>}
            </button>
            <button data-testid="nav-dashboard-btn" onClick={() => navigate('/auth')}
              className={`hidden sm:block px-4 py-2 rounded-md text-sm font-medium transition-all ${isDark ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'}`}>
              {t('nav.signin')}
            </button>
            <button data-testid="nav-menu-toggle" onClick={() => setMenuOpen(o => !o)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen}
              className={`md:hidden w-9 h-9 rounded-md flex items-center justify-center border transition-all ${isDark ? 'border-zinc-800 text-zinc-300' : 'border-zinc-200 text-zinc-700'}`}>
              {menuOpen ? <X size={17}/> : <Menu size={17}/>}
            </button>
            <button data-testid="nav-trial-btn" onClick={() => setShowTrial(true)}
              className="px-4 py-2 rounded-md text-sm font-semibold text-white transition-all hover:opacity-90"
              style={{ background:'linear-gradient(135deg,#002FA7,#00D4FF)' }}>
              {t('nav.start_trial')}
            </button>
          </div>
        </div>

        {/* Mobile menu: without this, a phone user cannot reach any section
            or sign in from the header. */}
        {menuOpen && (
          <div data-testid="mobile-menu" className="md:hidden px-4 pb-4 pt-1"
            style={{ background: isDark ? '#09090b' : '#ffffff', backdropFilter: 'none',
              boxShadow: '0 14px 30px -12px rgba(0,0,0,.35)',
              borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.08)'}` }}>
            <div className="flex flex-col">
              {[
                { key:'nav.products', href:'#products' },
                { key:'nav.flash_growth', href:'#flash-growth', label:'Flash Growth' },
                { key:'nav.pricing', href:'#pricing' },
                { key:'nav.case_studies', href:'#case-studies' },
              ].map(item => (
                <a key={item.key} href={item.href} onClick={() => setMenuOpen(false)}
                  data-testid={`mobile-nav-${item.href.slice(1)}`}
                  className={`py-3 text-sm font-semibold border-b ${isDark ? 'text-zinc-200 border-zinc-800' : 'text-zinc-700 border-zinc-100'}`}>
                  {item.label || t(item.key)}
                </a>
              ))}
              <button data-testid="mobile-nav-signin" onClick={() => { setMenuOpen(false); navigate('/auth'); }}
                className={`mt-3 py-2.5 rounded-md text-sm font-medium ${isDark ? 'bg-zinc-800 text-zinc-200' : 'bg-zinc-100 text-zinc-700'}`}>
                {t('nav.signin')}
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <main id="main">
      <section data-testid="hero-section" className="pt-28 pb-20 sm:pt-36 sm:pb-28 px-4 sm:px-8 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full opacity-10" style={{background:'radial-gradient(circle,#00D4FF,transparent 70%)'}}/>
          <div className="absolute bottom-20 right-1/4 w-80 h-80 rounded-full opacity-8" style={{background:'radial-gradient(circle,#C77DFF,transparent 70%)'}}/>
        </div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-6 ${isDark ? 'bg-zinc-800/60 text-zinc-400 border border-zinc-700' : 'bg-zinc-100 text-zinc-600 border border-zinc-200'}`}>
            <Sparkles size={12} style={{color:'#FFD166'}}/> {t('hero.badge')}
          </div>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95] mb-6">
            {t('hero.title_1')}<br/>{t('hero.title_2')} <span className="gradient-text">{t('hero.title_3')}</span>
          </h1>
          <p className={`text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed font-medium ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>
            {t('hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button data-testid="hero-trial-btn" onClick={() => setShowTrial(true)}
              className="px-8 py-3.5 rounded-md text-base font-semibold text-white flex items-center gap-2 transition-all hover:shadow-lg hover:shadow-blue-500/20"
              style={{ background:'linear-gradient(135deg,#002FA7,#00D4FF)' }}>
              {t('hero.cta_trial')} <ArrowRight size={18}/>
            </button>
            <button data-testid="hero-demo-btn" onClick={() => navigate('/auth')}
              className={`px-8 py-3.5 rounded-md text-base font-medium flex items-center gap-2 transition-all border ${isDark ? 'border-zinc-700 text-zinc-300 hover:border-zinc-500' : 'border-zinc-300 text-zinc-700 hover:border-zinc-400'}`}>
              <Play size={16}/> {t('hero.cta_demo')}
            </button>
          </div>
          <p className={`mt-5 text-xs ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
            {t('hero.note')}
          </p>
        </div>
      </section>

      {/* ── SOCIAL PROOF ── */}
      <section className={`py-12 border-y ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-zinc-50/50'}`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-8 text-center">
          <p className={`text-xs font-semibold uppercase tracking-widest mb-6 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
            {t('social.trusted')}
          </p>
          <div className={`flex flex-wrap items-center justify-center gap-x-10 gap-y-4 ${isDark ? 'opacity-70' : 'opacity-80'}`}>
            {['Fresh Greens','Del Monte','MetLife','Revolt EV','ITC'].map(name =>
              <span key={name} data-testid={`logo-${name.replace(/\s/g,'-').toLowerCase()}`}
                className={`text-base sm:text-lg font-bold tracking-wider ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>{name}</span>
            )}
          </div>
        </div>
      </section>

      {/* ── ECOSYSTEM ── */}
      <section id="products" data-testid="ecosystem-section" className="py-20 sm:py-28 px-4 sm:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4">{t('eco.title_1')}<br/>{t('eco.title_2')}</h2>
            <p className={`text-lg sm:text-xl max-w-xl mx-auto ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>
              {t('eco.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ecosystem.map((p, i) => (
              <div key={p.name} data-testid={`ecosystem-${p.name.toLowerCase()}`}
                className={`group relative p-8 rounded-xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${isDark ? 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-600' : 'border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-zinc-200/50'}`}
                style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="absolute top-0 left-0 w-full h-1 rounded-t-xl" style={{ background: p.color }} />
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `${p.color}15`, border: `1px solid ${p.color}30` }}>
                    <p.icon size={22} style={{ color: p.color }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-bold">{p.name}</h3>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                        style={{ background: `${p.color}15`, color: p.color, border: `1px solid ${p.color}30` }}>{p.tag}</span>
                    </div>
                    <p className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>{p.desc}</p>
                  </div>
                </div>
                <p className={`text-base leading-relaxed ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{p.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS (architecture + GDPR) ── */}
      <section id="how-it-works" data-testid="how-it-works-section" className={`py-20 sm:py-28 px-4 sm:px-8 relative overflow-hidden ${isDark ? '' : 'bg-white'}`}>
        <div className="absolute inset-0 pointer-events-none opacity-[0.05]"
          style={{ background:'radial-gradient(circle at 50% 30%, #00D4FF, transparent 60%)' }}/>
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-14">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-bold tracking-widest mb-4 ${isDark ? 'bg-zinc-800/60 text-zinc-300 border border-zinc-700' : 'bg-zinc-100 text-zinc-700 border border-zinc-200'}`}>
              <Sparkles size={11} style={{color:'#00D4FF'}}/> {t('how.eyebrow')}
            </div>
            <h2 className="text-3xl sm:text-5xl font-bold tracking-tight mb-5">
              {t('how.title')}
            </h2>
            <p className={`text-lg max-w-2xl mx-auto leading-relaxed ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>
              {t('how.subtitle')}
            </p>
          </div>

          {/* Architecture diagram (CSS-only) */}
          <div className={`relative rounded-2xl border p-6 sm:p-10 mb-12 ${isDark ? 'border-zinc-800 bg-zinc-900/40' : 'border-zinc-200 bg-zinc-50'}`}>
            <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-6 md:gap-4">
              {/* Left: humans + business */}
              <div className="flex flex-col items-center text-center">
                <div className="flex -space-x-3 mb-3">
                  {['#00D4FF','#C77DFF','#FFD166'].map((c,i)=> (
                    <div key={i} className="w-14 h-14 rounded-full border-2 flex items-center justify-center"
                      style={{ background:`${c}22`, borderColor:isDark?'#18181b':'#ffffff' }}>
                      <UserCog size={22} style={{color:c}}/>
                    </div>
                  ))}
                </div>
                <p className={`text-sm font-bold ${isDark ? 'text-zinc-200' : 'text-zinc-900'}`}>{t('how.step1.title')}</p>
                <p className={`text-xs mt-1 max-w-[200px] ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>+ Knowledge Base · Brand Docs · CRM History</p>
              </div>

              {/* Middle: OmniView orchestrator */}
              <div className="flex flex-col items-center text-center">
                <div className="hidden md:flex items-center justify-between w-full mb-3 px-2">
                  <ArrowRight size={18} className={isDark ? 'text-zinc-700' : 'text-zinc-400'}/>
                  <ArrowRight size={18} className={isDark ? 'text-zinc-700' : 'text-zinc-400'}/>
                </div>
                <div className="relative w-28 h-28 rounded-2xl flex items-center justify-center shadow-xl"
                  style={{ background:'linear-gradient(135deg,#002FA7,#00D4FF)' }}>
                  <span className="text-white font-black text-3xl">{'\u03A9'}</span>
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-green-500 border-2 flex items-center justify-center"
                    style={{ borderColor:isDark?'#18181b':'#ffffff' }}>
                    <Lock size={14} className="text-white"/>
                  </div>
                </div>
                <p className={`text-sm font-bold mt-4 ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>OmniView Orchestrator</p>
                <p className="text-xs mt-1" style={{color:'#06D6A0'}}>Stateless · Encrypted · You-owned</p>
              </div>

              {/* Right: encrypted vault */}
              <div className="flex flex-col items-center text-center">
                <div className={`w-16 h-20 rounded-lg border-2 flex flex-col items-center justify-center gap-1 mb-3 ${isDark ? 'border-green-700 bg-green-900/30' : 'border-green-300 bg-green-50'}`}>
                  <Server size={20} style={{color:'#06D6A0'}}/>
                  <Lock size={14} style={{color:'#06D6A0'}}/>
                </div>
                <p className={`text-sm font-bold ${isDark ? 'text-zinc-200' : 'text-zinc-900'}`}>Your Encrypted Server</p>
                <p className={`text-xs mt-1 max-w-[200px] ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Only you hold the keys. OmniView never sees your data.</p>
              </div>
            </div>
          </div>

          {/* 4-step explainer cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
            {howSteps.map((s, i) => (
              <div key={i} data-testid={`how-step-${i+1}`}
                className={`relative p-6 rounded-xl border transition-all duration-300 hover:-translate-y-1 ${isDark ? 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-600' : 'border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-md'}`}>
                <div className="text-[11px] font-bold mb-3" style={{color:s.color}}>STEP {String(i+1).padStart(2,'0')}</div>
                <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
                  style={{ background:`${s.color}18`, border:`1px solid ${s.color}40` }}>
                  <s.icon size={20} style={{color:s.color}}/>
                </div>
                <h3 className="text-base font-bold mb-2">{s.title}</h3>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{s.desc}</p>
              </div>
            ))}
          </div>

          {/* GDPR highlight */}
          <div data-testid="how-gdpr-card" className={`relative rounded-2xl border-2 p-6 sm:p-10 overflow-hidden ${isDark ? 'border-green-800 bg-green-950/20' : 'border-green-300 bg-green-50/70'}`}>
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10 pointer-events-none"
              style={{ background:'radial-gradient(circle,#06D6A0,transparent 70%)' }}/>
            <div className="relative flex flex-col md:flex-row items-start gap-6">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-green-900/50' : 'bg-white'} border-2`}
                style={{ borderColor:'#06D6A0' }}>
                <Shield size={26} style={{color:'#06D6A0'}}/>
              </div>
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-widest mb-3"
                  style={{ background:'#06D6A022', color: isDark?'#06D6A0':'#047857' }}>
                  <Lock size={10}/> {t('how.gdpr.badge')}
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-3 tracking-tight">{t('how.gdpr.title')}</h3>
                <p className={`text-base leading-relaxed mb-5 ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>{t('how.gdpr.desc')}</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  {['how.gdpr.point1','how.gdpr.point2','how.gdpr.point3','how.gdpr.point4'].map(k => (
                    <li key={k} className="flex items-start gap-2 text-sm">
                      <Check size={15} className="mt-0.5 flex-shrink-0" style={{color:'#06D6A0'}}/>
                      <span className={`font-medium ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>{t(k)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── LAUNCH IN 3 STEPS ── */}
      <section className={`py-20 sm:py-28 px-4 sm:px-8 ${isDark ? 'bg-zinc-900/30' : 'bg-zinc-50'}`}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4">{t('steps.title')}</h2>
            <p className={`text-lg max-w-lg mx-auto ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>
              {t('steps.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map(s => (
              <div key={s.step} data-testid={`step-${s.step}`}
                className={`relative p-8 rounded-xl border ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-white'}`}>
                <div className="text-4xl font-bold opacity-10 absolute top-4 right-6" style={{color:s.color}}>{s.step}</div>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                  style={{ background:`${s.color}15`, border:`1px solid ${s.color}30` }}>
                  <s.icon size={20} style={{color:s.color}}/>
                </div>
                <h3 className="text-lg font-bold mb-2">{s.title}</h3>
                <p className={`text-base leading-relaxed ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CASE STUDIES ── */}
      <section id="case-studies" data-testid="case-studies-section" className="py-20 sm:py-28 px-4 sm:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4">{t('cases.title')}</h2>
            <p className={`text-lg max-w-lg mx-auto ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>
              {t('cases.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {caseStudies.map((cs, i) => (
              <div key={i} data-testid={`case-study-${i}`}
                className={`rounded-xl border overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-white'}`}>
                <div className="h-1" style={{ background:cs.color }} />
                <div className="p-6 sm:p-8">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                      style={{ background:`${cs.color}15`, color:cs.color, border:`1px solid ${cs.color}30` }}>{cs.industry}</span>
                    <span className={`text-[10px] ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>{cs.period}</span>
                  </div>
                  <h3 className="text-lg font-bold mb-4">{cs.title}</h3>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {cs.results.map(r => (
                      <div key={r.label} className={`text-center p-3 rounded-lg ${isDark ? 'bg-zinc-800/50' : 'bg-zinc-50'}`}>
                        <div className="text-xl sm:text-2xl font-bold" style={{color:cs.color}}>{r.metric}</div>
                        <div className={`text-[10px] mt-1 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{r.label}</div>
                      </div>
                    ))}
                  </div>
                  <p className={`text-base leading-relaxed ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{cs.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FlashGrowthSection isDark={isDark} />

      {/* ── PRICING ── */}
      <section id="pricing" data-testid="pricing-section" className={`py-20 sm:py-28 px-4 sm:px-8 ${isDark ? 'bg-zinc-900/30' : 'bg-zinc-50'}`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4">{t('pricing.title')}</h2>
            <p className={`text-lg max-w-lg mx-auto mb-8 ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>
              {t('pricing.subtitle')}
            </p>
            <div className={`inline-flex items-center gap-3 p-1 rounded-lg ${isDark ? 'bg-zinc-800' : 'bg-zinc-200'}`}>
              <button data-testid="pricing-monthly" onClick={() => setAnnual(false)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${!annual ? 'bg-white text-zinc-900 shadow' : (isDark ? 'text-zinc-400' : 'text-zinc-500')}`}
                style={!annual ? {} : {background:'transparent'}}>{t('pricing.monthly')}</button>
              <button data-testid="pricing-annual" onClick={() => setAnnual(true)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${annual ? 'bg-white text-zinc-900 shadow' : (isDark ? 'text-zinc-400' : 'text-zinc-500')}`}
                style={annual ? {} : {background:'transparent'}}>
                {t('pricing.annual')} <span className="text-[10px] ml-1" style={{color:'#06D6A0'}}>{t('pricing.save')}</span>
              </button>
            </div>
            {/* Currency Switcher */}
            <div className={`inline-flex items-center gap-1 p-1 rounded-lg mt-3 ${isDark ? 'bg-zinc-800' : 'bg-zinc-200'}`}>
              {Object.keys(CURRENCIES).map(c => (
                <button key={c} data-testid={`currency-${c}`} onClick={() => setCurrency(c)}
                  className={`px-3 py-1 rounded-md text-xs font-semibold transition-all ${currency === c ? 'bg-white text-zinc-900 shadow' : (isDark ? 'text-zinc-400' : 'text-zinc-500')}`}
                  style={currency === c ? {} : { background: 'transparent' }}>
                  {CURRENCIES[c].symbol} {c}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {pricing.map(plan => (
              <div key={plan.name} data-testid={`plan-${plan.name.toLowerCase()}`}
                className={`relative rounded-xl border p-6 transition-all duration-300 hover:-translate-y-1 ${plan.popular ? 'ring-2' : ''} ${isDark ? 'border-zinc-800 bg-zinc-900/80' : 'border-zinc-200 bg-white'}`}
                style={plan.popular ? { ringColor:plan.color, boxShadow:`0 0 30px ${plan.color}15` } : {}}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-bold text-white"
                    style={{ background:plan.color }}>{t('pricing.popular')}</div>
                )}
                <div className="mb-4">
                  <h3 className="text-lg font-bold mb-1">{plan.name}</h3>
                  <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{plan.desc}</p>
                </div>
                <div className="mb-6">
                  <span className="text-3xl font-bold">{plan.price}</span>
                  <span className={`text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-6">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <Check size={15} className="mt-0.5 flex-shrink-0" style={{color:plan.color}}/>
                      <span className={`${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button data-testid={`plan-cta-${plan.name.toLowerCase()}`}
                  onClick={() => plan.name === 'Bespoke' ? window.open('mailto:hello@omniview.io?subject=Bespoke Plan Inquiry') : setShowTrial(true)}
                  className="w-full py-2.5 rounded-md text-sm font-semibold transition-all"
                  style={plan.popular ? { background:plan.color, color:'#fff' } : {
                    background:'transparent', border:`1px solid ${plan.color}`, color:plan.color }}>
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>

          <div className={`mt-10 text-center p-6 rounded-xl border ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-white'}`}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <div>
                <h4 className="font-bold text-lg mb-1">Open Source Self-Hosted Option</h4>
                <p className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>
                  One-time <span className="font-bold" style={{color:'#06D6A0'}}>Done-With-You</span> setup using Qwen 3.6 & Gemma 4.
                  Own your data. Run on your infrastructure. Grandfather pricing for founding users.
                </p>
              </div>
              <button data-testid="open-source-cta"
                onClick={() => window.open('mailto:hello@omniview.io?subject=Open Source Setup')}
                className={`whitespace-nowrap px-6 py-2.5 rounded-md text-sm font-semibold transition-all border ${isDark ? 'border-green-800 text-green-400 hover:bg-green-900/30' : 'border-green-300 text-green-700 hover:bg-green-50'}`}>
                Get Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── OPEN SOURCE ── */}
      <section className="py-20 sm:py-28 px-4 sm:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Cpu size={24} style={{color:'#C77DFF'}}/>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Built on Open Source</h2>
          </div>
          <p className={`text-lg max-w-2xl mx-auto mb-12 ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>
            We believe in transparency. OmniView&apos;s AI layer runs on the best open-source models.
            No vendor lock-in. You own your models, your data, your intelligence.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-xl mx-auto">
            {[
              { name:'Qwen 3.6', desc:'Next-gen reasoning model. Powers OmniThink analytics and blind spot detection.', color:'#00D4FF' },
              { name:'Gemma 4', desc:'Efficient deployment model. Powers OmniClaw agent actions and media plan generation.', color:'#06D6A0' },
            ].map(model => (
              <div key={model.name} data-testid={`model-${model.name.replace(' ','-').toLowerCase()}`}
                className={`p-6 rounded-xl border text-left ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-white'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full" style={{background:model.color}}/>
                  <h3 className="font-bold">{model.name}</h3>
                </div>
                <p className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{model.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-20 sm:py-28 px-4 sm:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-20"
          style={{ background:'radial-gradient(ellipse at center, #002FA7, transparent 70%)' }}/>
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-5xl font-bold tracking-tight mb-6">
            Ready to see what<br/>you&apos;re <span className="gradient-text">missing</span>?
          </h2>
          <p className={`text-lg mb-8 max-w-lg mx-auto ${isDark ? 'text-zinc-300' : 'text-zinc-600'}`}>
            Join the agencies and brands that have recovered $3M+ in hidden revenue through OmniView.
          </p>
          <button data-testid="final-cta-btn" onClick={() => setShowTrial(true)}
            className="px-10 py-4 rounded-md text-lg font-bold text-white transition-all hover:shadow-lg hover:shadow-blue-500/20"
            style={{ background:'linear-gradient(135deg,#002FA7,#00D4FF)' }}>
            Start Your 7-Day Free Trial <ArrowRight size={20} className="inline ml-2"/>
          </button>
        </div>
      </section>
      </main>

      {/* ── FOOTER ── */}
      <footer className={`py-12 px-4 sm:px-8 border-t ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-8 mb-10">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-bold"
                  style={{ background:'linear-gradient(135deg,#002FA7,#00D4FF)' }}>{'\u03A9'}</div>
                <span className="font-bold">OMNIVIEW</span>
              </div>
              <p className={`text-xs leading-relaxed ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
                The operating system for marketing intelligence.
              </p>
            </div>
            {[
              { title:'Products', items:[['OmniView','#features'],['OmniThink','#features'],['OmniPlan','#features'],['OmniClaw','#features']] },
              { title:'Resources', items:[['Blog','/blog'],['RSS Feed','rss'],['Flash Growth','#flash-growth'],['Pricing','#pricing']] },
              { title:'Company', items:[['About','#how-it-works'],['Contact','#pricing'],['Privacy Policy','#how-it-works']] },
            ].map(col => (
              <div key={col.title}>
                <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>{col.title}</h4>
                <ul className="space-y-2">
                  {col.items.map(([item, href]) => {
                    const cls = `text-sm transition-colors ${isDark ? 'text-zinc-500 hover:text-zinc-300' : 'text-zinc-500 hover:text-zinc-700'}`;
                    return (
                      <li key={item}>
                        {href === 'rss'
                          ? <a data-testid="footer-rss" href={`${process.env.REACT_APP_BACKEND_URL}/api/rss.xml`} target="_blank" rel="noreferrer" className={cls}>{item}</a>
                          : href.startsWith('/')
                            ? <Link data-testid={`footer-${item.toLowerCase()}`} to={href} className={cls}>{item}</Link>
                            : <a href={href} className={cls}>{item}</a>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
            <div className="col-span-2 sm:col-span-4 lg:col-span-1">
              <NewsletterSignup isDark={isDark} source="landing_footer" />
            </div>
          </div>
          <div className={`pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-4 ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
            <p className={`text-xs ${isDark ? 'text-zinc-700' : 'text-zinc-400'}`}>
              2025 OmniView. Built with open source AI. Qwen 3.6 & Gemma 4.
            </p>
            <div className="flex items-center gap-4">
              {['Twitter','LinkedIn','GitHub'].map(social =>
                <a key={social} href="#" className={`text-xs transition-colors ${isDark ? 'text-zinc-600 hover:text-zinc-400' : 'text-zinc-400 hover:text-zinc-600'}`}>{social}</a>
              )}
            </div>
          </div>
        </div>
      </footer>

      {/* ── TRIAL MODAL ── */}
      {showTrial && (
        <div data-testid="trial-modal" className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ background:isDark?'rgba(0,0,0,0.8)':'rgba(0,0,0,0.5)', backdropFilter:'blur(8px)' }}
          onClick={() => setShowTrial(false)}>
          <div onClick={e => e.stopPropagation()}
            className={`w-full max-w-md rounded-2xl p-8 animate-modalIn ${isDark ? 'bg-zinc-900 border border-zinc-800' : 'bg-white border border-zinc-200'}`}
            style={{ boxShadow:'0 25px 60px rgba(0,0,0,0.3)' }}>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-bold">Start Your Free Trial</h3>
                <p className={`text-sm mt-1 ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>7 days free. No credit card.</p>
              </div>
              <button data-testid="close-trial-modal" onClick={() => setShowTrial(false)}
                className={`w-8 h-8 rounded-md flex items-center justify-center ${isDark ? 'bg-zinc-800 text-zinc-400' : 'bg-zinc-100 text-zinc-500'}`}>
                <X size={16}/>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className={`text-xs font-medium block mb-1.5 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Work email</label>
                <div className={`flex items-center border rounded-md overflow-hidden ${isDark ? 'border-zinc-700 bg-zinc-800' : 'border-zinc-300 bg-white'}`}>
                  <Mail size={16} className={`ml-3 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}/>
                  <input data-testid="trial-email-input" type="email" placeholder="you@company.com"
                    value={email} onChange={e => setEmail(e.target.value)}
                    className={`flex-1 px-3 py-2.5 text-sm outline-none ${isDark ? 'bg-zinc-800 text-zinc-200 placeholder:text-zinc-600' : 'bg-white text-zinc-900 placeholder:text-zinc-400'}`}/>
                </div>
              </div>
              <button data-testid="trial-submit-btn" onClick={startTrial}
                className="w-full py-3 rounded-md text-sm font-bold text-white transition-all hover:opacity-90 flex items-center justify-center gap-2"
                style={{ background:'linear-gradient(135deg,#002FA7,#00D4FF)' }}>
                Start Free Trial <ArrowRight size={16}/>
              </button>
              <div className={`flex items-center gap-4 pt-2 ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
                {['No credit card','Cancel anytime','7-day trial'].map(t =>
                  <span key={t} className="text-[10px] flex items-center gap-1"><Check size={10}/>{t}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
